import { useQuery } from '@tanstack/react-query';
import { Challenge, Participant } from '@/types';
import { apiClient } from '@/lib/api';

// Optimized batch participants fetching with local-first approach
export const useBatchParticipants = (challenges: Challenge[]) => {
  // Single batch request for all participants - now truly local-first
  const batchQuery = useQuery({
    queryKey: ['batchParticipants', challenges.map(c => c.id).sort()],
    queryFn: async () => {
      console.log(`🚀 Loading participants for ${challenges.length} challenges (local-first)...`);
      const startTime = Date.now();

      // Fetch all participants in parallel - now returns local data immediately
      const results = await Promise.all(
        challenges.map(async challenge => {
          const participants = await apiClient.getChallengeParticipants(challenge.id);
          return participants;
        })
      );

      const participantsByChallenge = new Map<string, Participant[]>();

      results.forEach((participants, index) => {
        const challengeId = challenges[index].id;
        participantsByChallenge.set(challengeId, participants || []);
      });

      const loadTime = Date.now() - startTime;
      console.log(`⚡ Local participant data loaded in ${loadTime}ms`);

      return participantsByChallenge;
    },
    enabled: challenges.length > 0,
    staleTime: 30 * 1000, // 30 seconds - shorter for responsive homepage updates
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
    retry: 2
  });

  return {
    participantsByChallenge: batchQuery.data || new Map(),
    isLoading: batchQuery.isLoading,
    isError: batchQuery.isError,
    refetch: batchQuery.refetch,
  };
};
